import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  describeDeletionImpact,
  isVoidElement,
  readLockState,
  RUNTIME_MARKER_SLUG,
  removeElement,
  slugify,
} from '@/entities/section-picker';
import { buildPublicAssetUrl, markAssetPublic } from '@/shared/api/assets.api';
import type { ImageSourcePickerValue } from '@/shared/ui/ImageSourcePicker';
import { notificationService } from '@/lib/services/notification.service';
import { applyElementConfig, SELECTION_HELPER_ATTR } from '../../model/applyElementConfig';
import { toCustomAttributes, type CustomAttribute } from '../../model/customAttributes';
import { hasAttributeErrors } from './CustomAttributesEditor';
import type { ConfigureContentElementSheetProps } from './ConfigureContentElementSheet.types';

function sameAttributes(a: CustomAttribute[], b: CustomAttribute[]): boolean {
  return (
    a.length === b.length &&
    a.every((attr, i) => attr.name === b[i].name && attr.value === b[i].value)
  );
}

/**
 * All state, derived values, and handlers for configuring one content element — name it as an
 * updatable section, edit its copy or image, describe the image for the agent's picker, attach
 * client-specific attributes, and delete it. Shared by the Dialog and Sheet shells so the form
 * logic exists exactly once regardless of which chrome renders it.
 *
 * Callers must mount only while a target is selected — it seeds all of its state once, so
 * unmounting on close is what resets the form.
 */
export function useConfigureContentElementForm({
  target,
  html,
  existingSections,
  onClose,
  onApply,
}: ConfigureContentElementSheetProps) {
  const { t } = useTranslation('admin');

  const isImage = target?.tagName === 'img';
  const initialContent = target
    ? target.hasChildElements
      ? target.innerHTML
      : target.textContent
    : '';
  const initialDescription =
    target?.attributes.find((attr) => attr.name === SELECTION_HELPER_ATTR)?.value ?? '';
  const initialAttributes = useMemo(
    () => (target ? toCustomAttributes(target.attributes) : []),
    [target],
  );

  const [sectionName, setSectionName] = useState(target?.sectionName ?? '');
  const [content, setContent] = useState(initialContent);
  const [imageValue, setImageValue] = useState<ImageSourcePickerValue | null>(null);
  const [imageDescription, setImageDescription] = useState(initialDescription);
  const [attributes, setAttributes] = useState<CustomAttribute[]>(initialAttributes);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const slug = useMemo(() => slugify(sectionName), [sectionName]);
  // A row the user added but never named is inert: it neither counts as a change nor gets written.
  const namedAttributes = useMemo(
    () => attributes.filter((attribute) => attribute.name.trim()),
    [attributes],
  );
  // Gated on what the element CAN hold, not on what it currently holds. Gating on non-empty
  // content made emptying a field a one-way door — the textarea stopped rendering, so the only way
  // back was hand-editing in code view — and blocked seeding copy into a deliberately empty cell.
  const canEditContent = !isImage && !!target && !isVoidElement(target.tagName);

  const slugError = useMemo(() => {
    if (!slug || slug === target?.sectionName) return null;
    // Filtering the runtime's marker out on READ made a section literally named "tagged" invisible
    // AND unremovable — the panel never lists it, so there is no row to clear. Refusing it here is
    // the half that closes the trap.
    if (slug === RUNTIME_MARKER_SLUG) {
      return t('templatesConfig.configureElement.sectionNameReserved', { slug });
    }
    return existingSections.includes(slug)
      ? t('templatesConfig.configureElement.sectionNameTaken', { slug })
      : null;
  }, [slug, existingSections, target?.sectionName, t]);

  // No `slug.length > 0` guard: CLEARING the name is a change too — it untags the element.
  const tagChanged = slug !== (target?.sectionName ?? '');
  const contentChanged = canEditContent && content !== initialContent;
  const descriptionChanged = isImage && imageDescription.trim() !== initialDescription.trim();
  const attributesChanged = !sameAttributes(namedAttributes, initialAttributes);

  const canSave =
    !saving &&
    !slugError &&
    !hasAttributeErrors(attributes) &&
    (tagChanged || contentChanged || !!imageValue || descriptionChanged || attributesChanged);

  const handleSave = useCallback(async () => {
    if (!target || !canSave) return;
    setSaving(true);
    try {
      let imageSrc: string | null = null;
      if (imageValue) {
        if (imageValue.kind === 'url') {
          imageSrc = imageValue.url;
        } else {
          // The preview iframe and the published email both render this HTML directly, so the
          // asset has to be public and addressed absolutely — a relative or `asset://` src is
          // unresolvable in both places.
          await markAssetPublic(imageValue.assetId);
          imageSrc = buildPublicAssetUrl(imageValue.assetId);
        }
      }

      const result = applyElementConfig({
        html,
        selector: target.selector,
        currentSectionName: target.sectionName,
        originalAttributes: initialAttributes,
        draft: {
          sectionName: slug,
          content: contentChanged ? content : null,
          // The field holds markup only when the element has element children; otherwise it holds
          // decoded text that `applyElementConfig` must escape before splicing it back in.
          contentIsHtml: target.hasChildElements,
          imageSrc,
          imageDescription: isImage && descriptionChanged ? imageDescription : null,
          // Names are validated trimmed, so they must be written trimmed too — otherwise
          // `data-foo ` lands as `data-foo ="v"` and stops matching itself on the next edit.
          customAttributes: namedAttributes.map((attribute) => ({
            name: attribute.name.trim(),
            value: attribute.value,
          })),
        },
      });

      if (!result.success) {
        notificationService.showError(
          result.error ?? t('templatesConfig.configureElement.failedToApplyChanges'),
        );
        return;
      }

      onApply(result.html);
      onClose();
    } catch {
      notificationService.showError(t('templatesConfig.configureElement.failedToApplyChanges'));
    } finally {
      setSaving(false);
    }
  }, [
    target,
    canSave,
    imageValue,
    html,
    initialAttributes,
    slug,
    contentChanged,
    content,
    isImage,
    descriptionChanged,
    imageDescription,
    namedAttributes,
    onApply,
    onClose,
    t,
  ]);

  // What the delete would take with it, scanned over the span `removeElement` will actually cut.
  // One resolve answers both questions, over the exact span a delete would cut. `null` means the
  // span could not be resolved — which is why it also gates the button below: if we cannot say what
  // would disappear, we should not offer to make it disappear.
  const deletionImpact = useMemo(
    () => (target ? describeDeletionImpact(html, target.selector) : null),
    [html, target],
  );
  const sectionsAtRisk = deletionImpact?.sections ?? null;

  /**
   * Why deleting is unavailable, or null when it is fine.
   *
   * `readLockState` answers "is this element under a lock" — ancestors only. Deleting asks the
   * other direction too: a container is exactly what someone clicks when they mean to remove a
   * block, and the legal/footer copy a template author protects lives inside one.
   */
  const deleteBlockedReason = useMemo((): 'locked' | 'unresolvable' | null => {
    if (!target) return null;
    if (readLockState(html, target.selector).locked) return 'locked';
    if (deletionImpact === null) return 'unresolvable';
    return deletionImpact.hasLockedDescendant ? 'locked' : null;
  }, [target, html, deletionImpact]);

  const handleDelete = useCallback(() => {
    if (!target) return;
    const result = removeElement(html, target.selector);
    if (!result.success) {
      // Surfaced, never swallowed: a failed splice that reported success would leave the user
      // believing the element is gone while the template still holds it.
      notificationService.showError(
        result.error ?? t('templatesConfig.configureElement.failedToDeleteElement'),
      );
      setConfirmDeleteOpen(false);
      return;
    }
    onApply(result.html);
    notificationService.showSuccess(t('templatesConfig.configureElement.elementDeleted'));
    onClose();
  }, [target, html, onApply, onClose, t]);

  return {
    isImage,
    sectionName,
    setSectionName,
    content,
    setContent,
    imageValue,
    setImageValue,
    imageDescription,
    setImageDescription,
    attributes,
    setAttributes,
    saving,
    confirmDeleteOpen,
    setConfirmDeleteOpen,
    slugError,
    canEditContent,
    canSave,
    handleSave,
    sectionsAtRisk,
    deleteBlockedReason,
    handleDelete,
  };
}
